import {Trans, useLingui} from "@lingui/react/macro";
import {useContext, useEffect, useRef, type ReactNode} from "react";
import {flushSync} from "react-dom";
import type {GroupRepo, ItemId} from "../../models/GroupRepo";
import {AppShellContext} from "../../shared/ui/app-shell-context";
import {Col, Row} from "../../ui/Box";
import Button, {FloatingArea, FloatingAreaButton} from "../../ui/Button";
import Icon from "../../ui/Icon";
import {isActiveSearch, type RecipeSearch} from "./recipe-search";
import {recipeStyles as styles} from "./recipes.css";

// relative to the cook page, see `getModalRoutes` in GroupRecipes
const pickerPath = "ingredient-filter";
const viewPickerPath = "view";

type RecipeSearchBarProps = {
  // null while the search is closed
  search: RecipeSearch | null;
  onChange: (search: RecipeSearch | null) => void;
  focused: boolean;
  onFocusChange: (focused: boolean) => void;
  // the page's own floating button; hidden while typing so the field gets the full width
  actionButton: ReactNode;
  groupRepo: GroupRepo;
};

/**
 * Floating search field for the recipe list. It grows out of a button next to the page's floating
 * button and stays at the bottom, so it sits right above the keyboard and the filtered list stays
 * visible above it. Closing the keyboard keeps the filter; the ✕ clears it.
 *
 * Ingredients picked in a drawer show as removable pills above the field.
 */
const RecipeSearchBar = (props: RecipeSearchBarProps) => {
  const {search, onChange, focused, onFocusChange, actionButton, groupRepo} = props;
  const {t} = useLingui();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const barRef = useRef<HTMLElement | null>(null);
  const shell = useContext(AppShellContext);

  const handleOpen = () => {
    // iOS only opens the keyboard for a focus() inside the tap handler, so render the field now
    flushSync(() => onChange({query: "", itemIds: []}));
    // the field is sticky, so its resting place is the end of the list, where focus would scroll to
    inputRef.current?.focus({preventScroll: true});
  };

  const handleClear = () => {
    onChange(null);
    inputRef.current?.blur();
  };

  // Dragging the list while typing closes the keyboard, so the results get the whole screen.
  useEffect(() => {
    const content = shell?.contentRef.current;
    if (!focused || !content) return;
    const onTouchMove = (e: TouchEvent) => {
      if (barRef.current?.contains(e.target as Node)) return;
      inputRef.current?.blur();
    };
    content.addEventListener("touchmove", onTouchMove, {passive: true});
    return () => content.removeEventListener("touchmove", onTouchMove);
  }, [focused, shell]);

  const viewButton = (
    <span className={styles.solidBackdrop} style={{borderRadius: "9999px"}}>
      <FloatingAreaButton variant="secondary" to={viewPickerPath} aria-label={t`Order recipes`}>
        <Icon name="filter" />
      </FloatingAreaButton>
    </span>
  );

  if (search === null) {
    return (
      <FloatingArea>
        {viewButton}
        <span className={styles.solidBackdrop} style={{borderRadius: "9999px"}}>
          <FloatingAreaButton
            variant="secondary"
            onClick={handleOpen}
            aria-label={t`Search recipes`}
          >
            <Icon name="search" />
          </FloatingAreaButton>
        </span>
        {actionButton}
      </FloatingArea>
    );
  }

  const {query, itemIds} = search;
  const handleRemoveItem = (itemId: ItemId) => {
    const next = {query, itemIds: itemIds.filter((id) => id !== itemId)};
    // like blurring an empty field, removing the last filter closes the search
    onChange(focused || isActiveSearch(next) ? next : null);
  };

  return (
    <FloatingArea>
      {!focused && viewButton}
      <Col sp="8" flex="auto" minWidth="0" ref={barRef}>
        {(focused || itemIds.length > 0) && (
          // keeps the focus in the field, or blurring an empty field would close the bar before
          // the tap on a pill lands
          <div className={styles.filterPills} onMouseDown={(e) => e.preventDefault()}>
            {itemIds.map((itemId) => (
              <ItemPill
                key={itemId}
                itemId={itemId}
                groupRepo={groupRepo}
                onRemove={() => handleRemoveItem(itemId)}
              />
            ))}
            <span className={styles.solidBackdrop}>
              <Button variant="secondary" to={pickerPath}>
                <Row sp="4" align="center">
                  <Icon name="plus" size="16" />
                  <Trans>Ingredient</Trans>
                </Row>
              </Button>
            </span>
          </div>
        )}
        <Row align="center" pl="16" pr="8" sp="8" className={styles.searchField}>
          <Icon name="search" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              onChange({query: e.target.value, itemIds});
              shell?.contentRef.current?.scrollTo({top: 0});
            }}
            onFocus={() => onFocusChange(true)}
            onBlur={() => {
              onFocusChange(false);
              if (!isActiveSearch(search)) onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") handleClear();
              if (e.key === "Enter") inputRef.current?.blur();
            }}
            placeholder={t`Search recipes`}
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            className={styles.searchInput}
          />
          <Button variant="tertiary" onClick={handleClear} aria-label={t`Clear search`}>
            <Icon name="close" />
          </Button>
        </Row>
      </Col>
      {!focused && actionButton}
    </FloatingArea>
  );
};

type ItemPillProps = {itemId: ItemId; groupRepo: GroupRepo; onRemove: () => void};
const ItemPill = ({itemId, groupRepo, onRemove}: ItemPillProps) => {
  const {t} = useLingui();
  const item = groupRepo.Items.useById(itemId);
  const name = item?.name ?? "";
  return (
    <Button variant="secondary" active onClick={onRemove} aria-label={t`Remove ${name}`}>
      <Row sp="4" align="center">
        {item?.emoji && <span>{item.emoji}</span>}
        <span className={styles.pillName}>{name}</span>
        <Icon name="close" size="16" />
      </Row>
    </Button>
  );
};

export default RecipeSearchBar;
